import React, { useState, useEffect } from 'react';
import { GlobalMicroButton } from '../../GlobalMicroButton';
import { StatusBadge } from '../../StatusBadge';
import styles from './SOPCard.module.css';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { useTheme } from '../../../context/ThemeProvider';
import { StandardOperatingProcedure } from '../../../types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';

interface SOPCardProps {
  sop: StandardOperatingProcedure;
  projectId: string;
  onEdit?: (sop: StandardOperatingProcedure) => void;
  onDelete?: (sopId: string) => void;
  onView?: (sop: StandardOperatingProcedure) => void;
  onViewVersions?: (sop: StandardOperatingProcedure) => void;
  onEntityClick?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK') => void;
  compact?: boolean;
}

export const SOPCard: React.FC<SOPCardProps> = ({
  sop,
  projectId,
  onEdit,
  onDelete,
  onView,
  onViewVersions,
  onEntityClick,
  compact = false
}) => {
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [entityTitles, setEntityTitles] = useState<Record<string, string>>({});

  // Fetch entity titles for connected entities
  useEffect(() => {
    const fetchEntityTitles = async () => {
      if (!projectId || !isExpanded) return;

      const titles: Record<string, string> = {};

      // Fetch URS titles
      if (sop.links.urs_ids.length > 0) {
        try {
          const ursCol = collection(db, 'projects', projectId, 'urs');
          const ursSnapshot = await getDocs(ursCol);
          ursSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `URS-${doc.id.substring(0, 8)}`;
            if (sop.links.urs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching URS titles:', err);
          }
        }
      }

      // Fetch FS titles
      if (sop.links.fs_ids.length > 0) {
        try {
          const fsCol = collection(db, 'projects', projectId, 'fs');
          const fsSnapshot = await getDocs(fsCol);
          fsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            if (sop.links.fs_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching FS titles:', err);
          }
        }
      }

      // Fetch TEST titles
      if (sop.links.test_ids.length > 0) {
        try {
          const testCol = collection(db, 'projects', projectId, 'tests');
          const testSnapshot = await getDocs(testCol);
          testSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `TEST-${doc.id.substring(0, 8)}`;
            if (sop.links.test_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching TEST titles:', err);
          }
        }
      }

      // Fetch RISK titles
      if (sop.links.risk_ids.length > 0) {
        try {
          const riskCol = collection(db, 'projects', projectId, 'risks');
          const riskSnapshot = await getDocs(riskCol);
          riskSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
            if (sop.links.risk_ids.includes(id)) {
              titles[id] = data.title || id;
            }
          });
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Error fetching RISK titles:', err);
          }
        }
      }

      setEntityTitles(titles);
    };

    fetchEntityTitles();
  }, [projectId, sop.links, isExpanded]);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { textKey: string; variant: 'success' | 'info' | 'warning' | 'error' }> = {
      Effective: { textKey: 'global_status_active', variant: 'success' },
      Draft: { textKey: 'global_status_draft', variant: 'info' },
      'In Review': { textKey: 'global_status_in_review', variant: 'warning' },
      Obsolete: { textKey: 'global_status_rejected', variant: 'error' }
    };
    const statusInfo = statusMap[status] || { textKey: status, variant: 'info' as const };
    return <StatusBadge text={t(statusInfo.textKey)} variant={statusInfo.variant} size="medium" />;
  };

  const formatDate = (date: any) => {
    try {
      return new Date(date?.seconds ? date.seconds * 1000 : date).toLocaleDateString();
    } catch {
      return '';
    }
  };


  if (compact) {
    return (
      <div className={styles.sopCardCompact}>
        <div className={styles.compactHeader}>
          <span className={styles.sopId}>{sop.code}</span>
          {getStatusBadge(sop.status)}
        </div>
        <p className={styles.compactTitle}>{sop.title}</p>
        <div className={styles.compactMeta}>
          <span>{t('project_tab_effective_date') || 'Effective Date'}: {formatDate(sop.effective_date)}</span>
        </div>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.cardActions}`)) {
      return;
    }
    if (target.closest('button') && !target.closest(`.${styles.cardHeader}`)) {
      return;
    }
    if (target.closest(`.${styles.entityLinkItem}`)) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div 
      id={`entity-${sop.id}`}
      data-entity-card
      className={styles.sopCard}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          <h4 className={styles.sopId}>
            {sop.code}: {sop.title}
          </h4>
        </div>
        <div className={styles.headerRight}>
          {getStatusBadge(sop.status)}
          <div 
            data-expand-trigger
            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          >
            <img 
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"} 
              alt={isExpanded ? "Collapse" : "Expand"} 
              width={12} 
              height={12} 
            />
          </div>
        </div>
      </div>

      <p className={styles.description}>{sop.description || sop.title}</p>

      <div className={`${styles.sopDetails} ${isExpanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.sopDetailsInner}>
          {/* Inline meta fields */}
          {sop.meta?.department && (
            <div className={styles.inlineMeta}>
              <div>
                <span className={styles.metaLabel}>{t('project_tab_department') || 'Department'}:</span>
                <span className={styles.metaValue}> {sop.meta.department}</span>
              </div>
            </div>
          )}

          {/* Dates on separate line */}
          {(sop.createdAt || sop.updatedAt || sop.effective_date) && (
            <div className={styles.inlineMeta}>
              {sop.effective_date && (
                <div>
                  <span className={styles.metaLabel}>{t('project_tab_effective_date') || 'Effective Date'}:</span>
                  <span className={styles.metaValue}> {formatDate(sop.effective_date)}</span>
                </div>
              )}
              {sop.createdAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_created')}:</span>
                  <span className={styles.metaValue}> {formatDate(sop.createdAt)}</span>
                </div>
              )}
              {sop.updatedAt && (
                <div>
                  <span className={styles.metaLabel}>{t('global_updated')}:</span>
                  <span className={styles.metaValue}> {formatDate(sop.updatedAt)}</span>
                </div>
              )}
            </div>
          )}

          {/* Connected Entities */}
          {(sop.links.urs_ids.length > 0 || sop.links.fs_ids.length > 0 || sop.links.test_ids.length > 0 || sop.links.risk_ids.length > 0) && (
            <div className={styles.connectedEntitiesSection}>
              {sop.links.urs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_urs') || 'Linked URS'}:</span>
                  <div className={styles.entityList}>
                    {sop.links.urs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'URS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sop.links.fs_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_fs') || 'Linked FS'}:</span>
                  <div className={styles.entityList}>
                    {sop.links.fs_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'FS');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sop.links.test_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_tests') || 'Linked TEST'}:</span>
                  <div className={styles.entityList}>
                    {sop.links.test_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'TEST');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sop.links.risk_ids.length > 0 && (
                <div className={styles.entityGroup}>
                  <span className={styles.entityGroupLabel}>{t('project_tab_linked_risks') || 'Linked RISK'}:</span>
                  <div className={styles.entityList}>
                    {sop.links.risk_ids.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={styles.entityLinkItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityClick?.(id, 'RISK');
                        }}
                      >
                        {id}: {entityTitles[id] || id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Card actions */}
          <div 
            className={styles.cardActions}
            onClick={(e) => e.stopPropagation()}
          >

            {/* View button 
            {onView && (
              <GlobalMicroButton 
                onClick={() => onView(sop)}
                icon={<img src={isDarkMode ? "/assets/icons/eye.svg" : "/assets/icons/eye.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_view')}
              </GlobalMicroButton>
            )}
            {onViewVersions && (
              <GlobalMicroButton 
                onClick={() => onViewVersions(sop)}
                icon={<img src={isDarkMode ? "/assets/icons/clock.svg" : "/assets/icons/clock.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_versions')}
              </GlobalMicroButton>
            )}*/}

            {/* Edit button */}
            {onEdit && (
              <GlobalMicroButton 
                onClick={() => onEdit(sop)}
                icon={<img src={isDarkMode ? "/assets/icons/pencil.svg" : "/assets/icons/pencil.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_edit')}
              </GlobalMicroButton>
            )}

            {/* Delete button */}
            {onDelete && (
              <GlobalMicroButton 
                onClick={() => onDelete(sop.id)}
                icon={<img src={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt="" width={12} height={12} />}
              >
                {t('global_delete')}
              </GlobalMicroButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

